import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password, remember_me = false) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password, remember_me }, { withCredentials: true });
      if (data.two_factor_required) {
        return { success: false, twoFactorRequired: true, userId: data.user_id, email: data.email };
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const verifyTwoFactor = async (userId, code, remember_me = false) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/2fa/verify`,
        { user_id: userId, code, remember_me },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const resendTwoFactor = async (userId) => {
    try {
      await axios.post(`${API}/auth/2fa/resend`, { user_id: userId });
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const resendEmailVerification = async () => {
    try {
      await axios.post(`${API}/auth/email/resend-verification`, {}, { withCredentials: true });
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, payload, { withCredentials: true });
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const updateMarketingConsent = async (enabled) => {
    try {
      await axios.patch(`${API}/user/marketing-consent`, { enabled }, { withCredentials: true });
      setUser(p => p ? { ...p, marketing_consent: enabled } : p);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, verifyTwoFactor, resendTwoFactor, resendEmailVerification, updateMarketingConsent, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}
